// Appointment — read-only service details (/appointments/services/:serviceUid).
// Colored hero + spacious stat grid + weekly availability & closed dates.
// Actions menu (Edit / Closed dates / Pause / Delete) mirrors the list card.

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Loader2, CalendarClock, Clock, Users, CalendarDays, Timer, Hourglass, CalendarOff,
  MoreVertical, Pencil, Pause, Play, Trash2, Tag, ArrowLeft,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Topbar } from '@/components/Topbar';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  appointmentsService, WEEKDAY_NAMES, to12h, type ApptService,
} from '@/services/appointments.service';
import { ActionMenu } from './Popover';
import { useLabels, useCompanyCurrency } from '@/contexts/WorkspaceContext';

const Stat = ({ icon: Icon, label, value }: { icon: typeof Clock; label: string; value: string }) => (
  <div className="rounded-xl border border-border bg-surface px-4 py-3 flex items-center gap-3">
    <span className="w-9 h-9 rounded-xl bg-primary-soft text-primary flex items-center justify-center shrink-0"><Icon size={16} /></span>
    <span className="min-w-0">
      <span className="block text-[10.5px] uppercase tracking-wide text-text-muted">{label}</span>
      <span className="block text-[14px] font-bold text-text-primary truncate">{value}</span>
    </span>
  </div>
);

const ServiceView = () => {
  const companyCurrency = useCompanyCurrency();
  const t = useLabels();
  const navigate = useNavigate();
  const { serviceUid } = useParams<{ serviceUid: string }>();
  const [svc, setSvc] = useState<ApptService | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = () => {
    if (!serviceUid) return;
    setLoading(true);
    appointmentsService.getService(serviceUid)
      .then((r) => setSvc(r.item))
      .catch(() => { toast.error("Couldn't load service"); navigate('/appointments/services'); })
      .finally(() => setLoading(false));
  };
  useEffect(load, [serviceUid]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleActive = async () => {
    if (!svc) return;
    try {
      await appointmentsService.updateService(svc.serviceUid, {
        name: svc.name, description: svc.description ?? '', imageUrl: svc.imageUrl ?? undefined,
        durationMinutes: svc.durationMinutes, price: svc.price ?? undefined, currency: svc.currency ?? undefined,
        capacityPerSlot: svc.capacityPerSlot, maxAdvanceDays: svc.maxAdvanceDays, minLeadMinutes: svc.minLeadMinutes,
        bufferBeforeMin: svc.bufferBeforeMin, bufferAfterMin: svc.bufferAfterMin,
        hours: svc.hours, isActive: !svc.isActive,
      });
      toast.success(svc.isActive ? 'Bookings paused' : 'Now accepting bookings');
      load();
    } catch (e: any) {
      toast.error(`Couldn't update — ${e?.response?.data?.message ?? 'Try again.'}`);
    }
  };
  const confirmDelete = async () => {
    if (!svc) return;
    try {
      await appointmentsService.deleteService(svc.serviceUid);
      toast.success('Service deleted');
      navigate('/appointments/services');
    } catch (e: any) {
      toast.error(`Couldn't delete — ${e?.response?.data?.message ?? 'Try again.'}`);
    }
    setDeleteOpen(false);
  };

  return (
    <>
      <Topbar
        title={svc?.name || t('services', 'singular')}
        breadcrumb={`${t('appointments')} / ${t('services')}`}
        description="Service details."
        back={
          <button
            onClick={() => navigate('/appointments/services')}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary transition-colors"
            aria-label="Back"
          >
            <ArrowLeft size={16} />
          </button>
        }
        right={svc ? (
          <ActionMenu
            trigger={
              <button type="button" className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface text-text-muted hover:bg-surface-alt hover:text-text-primary transition-colors">
                <MoreVertical size={16} />
              </button>
            }
            items={[
              { label: 'Edit service', icon: <Pencil size={14} />, onSelect: () => navigate(`/appointments/services/${svc.serviceUid}/edit`) },
              { label: 'Closed dates', icon: <CalendarOff size={14} />, onSelect: () => navigate(`/appointments/services/${svc.serviceUid}/edit`) },
              {
                label: svc.isActive ? 'Pause bookings' : 'Accept bookings',
                icon: svc.isActive ? <Pause size={14} /> : <Play size={14} />,
                onSelect: () => void toggleActive(),
              },
              { label: 'Delete service', icon: <Trash2 size={14} />, danger: true, separatorAbove: true, onSelect: () => setDeleteOpen(true) },
            ]}
          />
        ) : undefined}
      />
      <div className="w-full px-7 py-6">
        {loading || !svc ? (
          <div className="flex items-center justify-center gap-2 py-24 text-text-muted"><Loader2 size={18} className="animate-spin" /> Loading…</div>
        ) : (
          <div className="flex flex-col gap-5 w-full max-w-[1000px] mx-auto">
            {/* hero — coloured band + avatar */}
            <div className="relative rounded-2xl overflow-hidden border border-border bg-gold-gradient">
              {/* soft decorative glow */}
              <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full bg-white/10 blur-2xl pointer-events-none" />
              <div className="relative z-10 p-6 flex items-center gap-5 text-white">
                {/* avatar — full image contained on white, so photos AND logos show whole */}
                <div className="w-24 h-24 rounded-2xl bg-white/95 ring-4 ring-white/25 shrink-0 flex items-center justify-center overflow-hidden shadow-elevated">
                  {svc.imageUrl
                    ? <img src={svc.imageUrl} alt="" className="w-full h-full object-contain" />
                    : <CalendarClock size={40} strokeWidth={1.4} className="text-primary" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={clsx('inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2.5 py-1',
                      svc.isActive ? 'bg-emerald-500/90' : 'bg-slate-700/70')}>
                      <span className="w-1.5 h-1.5 rounded-full bg-white" /> {svc.isActive ? 'Accepting bookings' : 'Paused'}
                    </span>
                    {svc.price != null && (
                      <span className="inline-flex items-center gap-1 text-[12px] font-bold rounded-full px-2.5 py-1 bg-white/20">
                        <Tag size={11} /> {svc.currency || companyCurrency} {svc.price}
                      </span>
                    )}
                  </div>
                  <h1 className="text-[26px] font-extrabold leading-tight">{svc.name}</h1>
                  {svc.description && <p className="text-[13px] text-white/85 mt-1 max-w-[820px] leading-relaxed line-clamp-2">{svc.description}</p>}
                </div>
              </div>
            </div>

            {/* booking rules — full-width row of stat cards */}
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wide text-text-muted mb-2 px-1">Booking rules</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <Stat icon={Clock} label="Duration" value={`${svc.durationMinutes} min`} />
                <Stat icon={Timer} label="Break between slots" value={svc.bufferAfterMin > 0 ? `${svc.bufferAfterMin} min` : 'None'} />
                <Stat icon={Users} label="Max per slot" value={`${svc.capacityPerSlot}`} />
                <Stat icon={CalendarDays} label="Book up to" value={`${svc.maxAdvanceDays} days`} />
                <Stat icon={Hourglass} label="Min. notice" value={svc.minLeadMinutes > 0 ? `${svc.minLeadMinutes} min` : 'None'} />
                <Stat icon={CalendarClock} label="Total bookings" value={`${svc.bookingCount ?? 0}`} />
              </div>
            </div>

            {/* weekly availability — full-width 7-day strip */}
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wide text-text-muted mb-2 px-1">Weekly availability</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                {WEEKDAY_NAMES.map((dayName, wd) => {
                  const day = svc.hours?.find((h) => h.weekday === wd);
                  const open = day?.isOpen && (day.windows?.length ?? 0) > 0;
                  return (
                    <div key={wd} className={clsx('rounded-2xl border overflow-hidden flex flex-col', open ? 'border-primary/25 bg-primary-soft/30' : 'border-border bg-surface-alt/30')}>
                      <div className={clsx('px-3 py-2 text-[12px] font-bold text-center', open ? 'bg-primary text-on-primary' : 'bg-surface-alt text-text-muted')}>{dayName}</div>
                      <div className="p-2.5 flex flex-col gap-1.5 flex-1 min-h-[64px]">
                        {open ? day!.windows.map((w, i) => (
                          <span key={i} className="text-[11.5px] font-medium text-text-primary bg-surface border border-border rounded-lg px-2 py-1 text-center">{to12h(w.start)}<br />{to12h(w.end)}</span>
                        )) : <span className="text-[11.5px] text-text-muted text-center my-auto">Closed</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* closed dates — full width */}
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wide text-text-muted mb-2 px-1">Closed dates</div>
              {(svc.closures?.length ?? 0) === 0 ? (
                <div className="rounded-2xl border border-border bg-surface px-4 py-3 flex items-center gap-2 text-[12.5px] text-text-muted"><CalendarOff size={14} /> No closed dates scheduled.</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {svc.closures!.map((c) => (
                    <div key={c.closureUid} className="inline-flex items-center gap-2 rounded-xl border border-border bg-surface px-3 py-2 text-[12.5px]">
                      <CalendarOff size={13} className="text-primary shrink-0" />
                      <span className="font-semibold text-text-primary whitespace-nowrap">{c.dateFrom}{c.dateTo !== c.dateFrom ? ` → ${c.dateTo}` : ''}</span>
                      {c.scope === 'COMPANY' && <span className="text-[9px] font-bold uppercase text-text-muted bg-surface-alt rounded-full px-1.5 py-0.5 shrink-0">All</span>}
                      {c.reason && <span className="text-text-muted">· {c.reason}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteOpen}
        title={`Delete "${svc?.name ?? ''}"?`}
        message="The service stops accepting bookings everywhere. Existing bookings are kept. This can't be undone."
        danger
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleteOpen(false)}
      />
    </>
  );
};

export default ServiceView;
